import { useRemoveSavedProductPrices, useSaveProductPrices } from "@fero/hooks";
import { usePurchaserSalePriceStore } from "@fero/stores";
import type { ProductSalePriceInfo } from "@fero-shared/api-contracts";

export const usePurchaserSalePriceHelpers = () => {
  const {
    organisationId,
    purchaserId,
    salePricesToSave,
    salePricesToRemove,
    setSalePricesToSave,
    setSalePricesToRemove,
  } = usePurchaserSalePriceStore();
  const removeProductSalePricesMutation = useRemoveSavedProductPrices();
  const saveProductSalePricesMutation = useSaveProductPrices();

  const addSalePrice = (productSalePrice: ProductSalePriceInfo) => {
    const salePrice = salePricesToSave.find(
      (salePrice) =>
        salePrice.productId === productSalePrice.productId &&
        salePrice.currency === productSalePrice.currency,
    );
    if (salePrice) {
      setSalePricesToSave(
        salePricesToSave.map((salePrice) => {
          if (
            salePrice.productId === productSalePrice.productId &&
            salePrice.currency === productSalePrice.currency
          ) {
            return productSalePrice;
          }
          return salePrice;
        }),
      );

      return;
    }

    setSalePricesToSave([...salePricesToSave, productSalePrice]);
    setSalePricesToRemove(
      salePricesToRemove.filter(
        (productId) => productId !== productSalePrice.productId,
      ),
    );
  };

  const updateSalePrice = (
    productIdToUpdate: string,
    updatedSalePrice: Partial<ProductSalePriceInfo>,
  ) => {
    const updatedSalePrices = salePricesToSave.map((salePrice) => {
      if (salePrice.productId === productIdToUpdate) {
        return { ...salePrice, ...updatedSalePrice };
      }
      return salePrice;
    });
    setSalePricesToSave(updatedSalePrices);
  };

  /**
   * Removes a sale price from the list "to save" if it was added by the user
   * Calls the "remove" endpoint if the sale price was already saved
   */
  const removeSalePrice = async (productId: string) => {
    const updatedSalePrices = salePricesToSave.filter(
      (salePrice) => salePrice.productId !== productId,
    );

    setSalePricesToSave(updatedSalePrices);
    setSalePricesToRemove([...salePricesToRemove, productId]);
  };

  /**
   * Saves all the sale prices in the list "to save"
   * Resets the list "to save" after saving
   */
  const saveProductSalePrices = async () => {
    if (salePricesToSave.length > 0) {
      await saveProductSalePricesMutation.mutateAsync({
        body: { organisationId, purchaserId, salePrices: salePricesToSave },
      });
    }
    if (salePricesToRemove.length > 0) {
      await removeProductSalePricesMutation.mutateAsync({
        body: { organisationId, purchaserId, productIds: salePricesToRemove },
      });
    }

    setSalePricesToSave([]);
    setSalePricesToRemove([]);
  };

  return {
    addSalePrice,
    updateSalePrice,
    removeSalePrice,
    saveProductSalePrices,
  };
};
